import { create } from "zustand"
import { track } from "@/shared/lib/analytics"
import {
  getAuthModalInitialType,
  shouldTrackAuthModalDismiss,
  type AuthModalType,
} from "@/shared/lib/analytics/auth-modal"
import type { PaywallIntent } from "@/shared/lib/paywall"

export type { PaywallIntent } from "@/shared/lib/paywall"

/** Context passed to the paywall modal so it can render character-aware copy + social proof. */
export interface PaywallContext {
  intent: PaywallIntent
  trigger: string
  characterId: string | null
  characterName: string | null
  characterAvatar: string | null
  messagesSentCount: number | null
  paywallExperimentFlag?: string
  paywallExperimentVariant?: string
}

export interface AuthModalContext {
  characterId: string | null
  characterName: string | null
  characterAvatar: string | null
  source?: "chat" | "character" | "subscription" | "generic"
}

export type SettingsSectionId = "account" | "data-controls" | "billing"

export interface SettingsModalContext {
  source?: "navigation" | "profile" | "chat" | "billing" | "url"
}

interface UIState {
  sidebarOpen: boolean
  sidebarCollapsed: boolean
  mobileMenuOpen: boolean
  mobileNavOpen: boolean
  activeModal: string | null
  /** A protected Explore action asked to continue onboarding. */
  onboardingRequested: boolean
  /** True once the user dismisses the register modal — auth gates fire at most once per session. */
  registerModalDismissed: boolean
  /** Auth form that began the current modal lifecycle, retained through login/register switches. */
  authModalInitialType: AuthModalType | null
  /** True once the current auth modal reached a login/signup completion, so closing it is not counted as an abandon. */
  authModalCompleted: boolean
  paywallContext: PaywallContext | null
  authContext: AuthModalContext | null
  settingsSection: SettingsSectionId
  settingsContext: SettingsModalContext | null
  toggleSidebar: () => void
  toggleSidebarCollapsed: () => void
  setSidebarCollapsed: (collapsed: boolean) => void
  toggleMobileMenu: () => void
  setMobileNavOpen: (open: boolean) => void
  setActiveModal: (modal: string | null) => void
  setOnboardingRequested: (requested: boolean) => void
  setRegisterModalDismissed: (dismissed: boolean) => void
  markAuthModalCompleted: () => void
  setPaywallContext: (ctx: PaywallContext | null) => void
  setAuthContext: (ctx: AuthModalContext | null) => void
  openSettings: (section?: SettingsSectionId, context?: SettingsModalContext | null) => void
  setSettingsSection: (section: SettingsSectionId) => void
  closeModal: () => void
}

export const useUIStore = create<UIState>((set, get) => ({
  sidebarOpen: true,
  sidebarCollapsed: false,
  mobileMenuOpen: false,
  mobileNavOpen: false,
  activeModal: null,
  onboardingRequested: false,
  registerModalDismissed: false,
  authModalInitialType: null,
  authModalCompleted: false,
  paywallContext: null,
  authContext: null,
  settingsSection: "account",
  settingsContext: null,

  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),

  toggleSidebarCollapsed: () =>
    set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

  setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),

  toggleMobileMenu: () =>
    set((state) => ({ mobileMenuOpen: !state.mobileMenuOpen })),

  setMobileNavOpen: (mobileNavOpen) => set({ mobileNavOpen }),

  setActiveModal: (activeModal) =>
    set((state) => ({
      activeModal,
      authModalInitialType: getAuthModalInitialType(
        state.activeModal,
        state.authModalInitialType,
        activeModal
      ),
      // Starting a new lifecycle must not inherit completion from a prior modal.
      authModalCompleted:
        state.activeModal === "login" || state.activeModal === "register"
          ? state.authModalCompleted
          : false,
    })),

  setOnboardingRequested: (onboardingRequested) => set({ onboardingRequested }),

  setRegisterModalDismissed: (registerModalDismissed) =>
    set({ registerModalDismissed }),

  markAuthModalCompleted: () => set({ authModalCompleted: true }),

  setPaywallContext: (paywallContext) => set({ paywallContext }),

  setAuthContext: (authContext) => set({ authContext }),

  openSettings: (settingsSection = "account", settingsContext = null) =>
    set({ activeModal: "settings", settingsSection, settingsContext: settingsContext ?? null }),

  setSettingsSection: (settingsSection) => set({ settingsSection }),

  closeModal: () => {
    const { activeModal, authModalInitialType, authContext, authModalCompleted } = get()

    // Closing the login/register modal without a completed login/signup is an
    // abandon — the open→abandon pair is otherwise only visible in session replay.
    if (shouldTrackAuthModalDismiss(activeModal, authModalCompleted)) {
      track("auth:modal_dismiss", {
        modal: authModalInitialType ?? activeModal,
        source: authContext?.source,
      })
    }

    set((state) => ({
      activeModal: null,
      paywallContext: null,
      authContext: null,
      settingsContext: null,
      authModalInitialType: null,
      authModalCompleted: false,
      // Remember a register-modal dismissal so secondary gates stop re-popping it.
      registerModalDismissed:
        state.activeModal === "register" || state.registerModalDismissed,
    }))
  },
}))
